/**
 * Deployment stage this bundle was built for, reported to PostHog as a super property so events can
 * be filtered by environment.
 *
 * Falls back to `NEXT_PUBLIC_SENTRY_ENVIRONMENT` because that is the one stage marker already set in
 * every environment (`.env.dist`, `sentry.*.config.js`), so this needs no new deployment config.
 * `NEXT_PUBLIC_ENVIRONMENT` is the non-Sentry-specific name to migrate to.
 */
export const ANALYTICS_ENVIRONMENT =
    process.env.NEXT_PUBLIC_ENVIRONMENT ?? process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? 'unknown';

/**
 * Whether to talk to PostHog at all. Every PostHog call site must gate on this same constant.
 *
 * Lives in `composables` rather than in one app because the shared booking flow reads feature flags
 * too, and an app that never calls `posthog.init` (the cinema websites set no PostHog key) must not
 * enroll its visitors in an experiment whose events nothing would ever send.
 *
 * Mirrors the mobile app (`cineamo-app/apps/cineamo/src/config/posthog-init.ts`), which has always
 * disabled PostHog outside staging/production. Web did not: the key lives in a gitignored `.env`, so
 * any developer holding a copy silently enrolled themselves in live experiments — an exposure fires
 * on mounting the program page — and their clicks counted as that variant's metric events.
 *
 * Only an exact 'local' match disables. Failing open means a deployment that forgets the variable
 * keeps reporting, which is far cheaper than production analytics going quietly dark.
 *
 * Deliberately env-var-only, with no `window`/`location` check: this is evaluated during SSR too and
 * the program page picks a layout from it, so a value that disagreed between server and client would
 * hydrate wrong. Host-based exclusion belongs in PostHog's own test-account filter, not here.
 */
export const IS_ANALYTICS_ENABLED = Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY) && ANALYTICS_ENVIRONMENT !== 'local';
